import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { Observable, PartialObserver, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ThemeService } from '../../services/theme.service';
import { environment } from './../../../environments/environment';
import { IMember, IMemberResult } from './../../models/member';
import { IOrganization, IOrganizationBase } from './../../models/organization';
import { Preferences } from './../../models/preference-constants';
import { GoogleAnalyticsService } from './../../services/google-analytics/google-analytics.service';
import { MemberService } from './../../services/member/member.service';
import { UserService } from './../../services/user/user.service';
import { App } from './../../state/app.actions';
import { ApplicationState } from './../../state/app.state';
import { UserState } from './../../users/user.state';
import { BaseComponent } from '../../shared/components/base/base.component';
import { SaleCampaign } from '../../sale-campaign/state/sale-campaign.actions';
import { SaleCampaignState } from '../../sale-campaign/state/sale-campaign.state';
import { ISaleCampaign } from '../../models/sale-campaign';
import { SaleCampaignService } from '../../services/sale-campaign/sale-campaign.service';
import { ExpenseMatchService } from '../../services/expense-match/expense-match.service';
import {
  ArrayUtilities,
  SortOrder,
} from '../../shared/utilities/array-utilities';
import { UrlUtilities } from '../../shared/utilities/url-utilities';

@Component({
  selector: 'app-sidebar',
  providers: [UserService],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent
  extends BaseComponent
  implements AfterViewInit, OnDestroy, OnInit
{
  @Input() sidebarVisible: boolean = true;
  @Input() navTab: string = 'menu';
  @Input() currentActiveMenu;
  @Input() currentActiveSubMenu;
  @Output() changeNavTabEvent = new EventEmitter();
  @Output() activeInactiveMenuEvent = new EventEmitter();
  public themeClass: string = 'theme-blue';
  public darkClass: string = '';
  private ngUnsubscribe = new Subject<void>();
  public user: IMember;
  public preferenceForm: UntypedFormGroup;

  @Select(UserState.getCurrentUser)
  getSidebarUser$: Observable<IMember>;

  @Select(ApplicationState.getCurrentOrganization)
  currentOrganization$: Observable<IOrganizationBase>;

  @Select(App.Actions.GetHasOrganization)
  hasOrganization$: Observable<boolean>;
  hasOrganization: boolean = true;

  @Select(ApplicationState.hasOrganizationValue)
  getHasOrganiationValue$: Observable<boolean>;

  @Select(SaleCampaignState.getCampaignList)
  campaignList$: Observable<ISaleCampaign[]>;

  sendEmail: boolean;
  sendSms: boolean;
  // organization: IOrganizationBase;
  organizationId: string;
  campaigns: ISaleCampaign[];
  activeCampaigns: ISaleCampaign[];
  unmatchedCount: number = 0;
  expenseMatchPendingCount: number = 0;
  isCampaignListExpanded: boolean = true;
  selectedCampaignKey: string = '';
  get showMessages(): boolean {
    return environment.featureFlags.messages;
  }

  get showAttendance(): boolean {
    return environment.featureFlags.attendance;
  }

  get showVoting(): boolean {
    return environment.featureFlags.voting;
  }

  get showBudgetTools(): boolean {
    return environment.featureFlags.budgetTools && this.organizationId === '69ab2e278b9ce8fe4d7becec';
  }

  get profileUrl(): string {
    if (
      this.user &&
      this.user.preferences &&
      this.user.preferences.profileImage
    )
      return `${environment.dbApi.baseUrl}/documents/read/?file=${this.user.preferences.profileImage}`;
    else return '';
  }

  constructor(
    private themeService: ThemeService,
    // private userService: UserService,
    private memberService: MemberService,
    private cdr: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    public googleAnalyticsService: GoogleAnalyticsService,
    private store: Store,
    private saleCampaignService: SaleCampaignService,
    private expenseMatchService: ExpenseMatchService
  ) {
    super();
    this.themeService.themeClassChange
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((themeClass) => {
        this.themeClass = themeClass;
      });
    this.themeService.darkClassChange
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((darkClass) => {
        this.darkClass = darkClass;
      });
    this.subscribeToCampaignList();
  }
  ngAfterViewInit(): void {
    let currentTab = 'menu';
    let currentTheme = '';

    if (localStorage.getItem('side-tab')) {
      currentTab = localStorage.getItem('side-tab');
    }

    if (localStorage.getItem('menu-item')) {
      const menuItem = localStorage.getItem('menu-item');
      setTimeout(() => this.activeInactiveMenuEvent.emit({ item: menuItem }));
    }

    if (localStorage.getItem('theme')) {
      currentTheme = localStorage.getItem('theme');
    }

    if (
      this.user &&
      this.user['preferences'] &&
      this.user['preferences']['theme']
    ) {
      currentTheme = this.user['preferences']['theme'];
    }
    let that = this;

    setTimeout(function () {
      that.changeNavTab(currentTab);
    }, 100);

    setTimeout(function () {
      that.themeService.changeDarkMode(currentTheme);
    }, 10);
  }
  ngOnInit(): void {
    this.getSidebarUser$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        if (!user) return;
        this.user = user;

        if (this.user && this.user['organizations']) {
          this.hasOrganization = this.user['organizations'].length > 0;
        }
      });

    this.userService.currentUser$.subscribe({
      next: (user) => {
        if (!user) return;
        this.user = user;
      },
    });

    this.hasOrganization$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((hasOrganization) => {
        this.hasOrganization = hasOrganization;

        if (!this.hasOrganization) {
        }
      });
    this.userService.hasOrganization$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((hasOrg) => {
        this.hasOrganization = hasOrg;
      });

    this.getHasOrganiationValue$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((hasOrganization) => {
        this.hasOrganization = hasOrganization;
      });

    this.initializeForm();
    this.initSubscribers();
  }

  get fullName(): string {
    return !!this.user ? `${this.user.firstname} ${this.user.lastname}` : '';
  }

  initSubscribers(): void {
    this.currentOrganization$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (organization) => {
          this.cdr.detectChanges();
          if (organization) {
            this.organization = organization as unknown as IOrganization;
            this.organizationId = organization.id;

            this.store.dispatch(
              new SaleCampaign.Actions.GetCampaigns(this.organization.id)
            );

            this.saleCampaignService
              .getUnmatchedPayments(this.organization.id)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe({ next: (data) => { this.unmatchedCount = data.length; } });

            // Seeds pendingCount$ for this org via getPendingCount()'s own tap().
            this.expenseMatchService.refreshPendingCount();
          }
        },
      });

    // Stays subscribed for the component's lifetime so the badge updates
    // whenever a match is confirmed/rejected/kept-separate/unlinked anywhere
    // in the app — not just when this component first loads or the org
    // changes. ExpenseMatchService is providedIn: 'root', so this Subject is
    // shared across every component that injects the service.
    this.expenseMatchService.pendingCount$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({ next: (count) => { this.expenseMatchPendingCount = count; } });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  changeNavTab(tab: string) {
    this.navTab = tab;
    localStorage.setItem('side-tab', tab);
  }

  activeInactiveMenu(menuItem: string) {
    const nextMenu = this.currentActiveMenu === menuItem ? '' : menuItem;

    this.currentActiveMenu = nextMenu;
    localStorage.setItem('menu-item', nextMenu);
    this.activeInactiveMenuEvent.emit({ item: nextMenu });
  }

  onNavTabKeydown(event: KeyboardEvent, tab: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.changeNavTab(tab);
    }
  }

  onMenuToggleKeydown(event: KeyboardEvent, menuItem: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.activeInactiveMenu(menuItem);
    }
  }

  toggleCampaignList(): void {
    this.isCampaignListExpanded = !this.isCampaignListExpanded;
  }

  onCampaignListKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleCampaignList();
    }
  }

  getCampaignKey(campaign: ISaleCampaign): string {
    if (!campaign) return '';

    return (
      (campaign as any).id ||
      (campaign as any)._id ||
      campaign.title ||
      ''
    );
  }

  selectCampaign(campaign: ISaleCampaign): void {
    this.selectedCampaignKey = this.getCampaignKey(campaign);
  }

  isCampaignSelected(campaign: ISaleCampaign): boolean {
    return this.selectedCampaignKey === this.getCampaignKey(campaign);
  }

  changeTheme(theme: string) {
    this.themeService.themeChange(theme);
  }

  changeDarkMode(darkClass: string) {
    this.themeService.changeDarkMode(darkClass);
    localStorage.setItem('theme', darkClass);
    this.updatePreference(Preferences.Theme, darkClass);
  }

  private subscribeToCampaignList() {
    this.campaignList$
      .pipe(
        takeUntil(this.unSubscribeAll$),
        distinctUntilChanged(),
        debounceTime(300)
      )
      .subscribe((campaigns) => {
        if (!campaigns || campaigns.length === 0) return;
        // console.log('campaigns subscribeToCampaignList', campaigns);
        this.campaigns = campaigns.map((campaign) => {
          return {
            ...campaign,
            url: UrlUtilities.getCampaignUrl(campaign),
          } as ISaleCampaign;
        });

        const currentDate = new Date();
        const comparisonDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate() + 1
        );
        this.activeCampaigns = campaigns.filter(
          (campaign) =>
            !campaign.endDate || new Date(campaign.endDate) >= comparisonDate
        );

        this.activeCampaigns = ArrayUtilities.sort(
          this.activeCampaigns,
          'title',
          SortOrder.asc
        );
      });
  }

  initializeForm(): void {
    if (this.user && this.user.communication) {
      this.sendSms = this.user.communication.sms;
      this.sendEmail = this.user.communication.email;
    }

    if (this.user) {
      const preference = this.user['preferences'];
      if (preference && preference['theme']) {
        this.darkClass = preference['theme'];
      }
    }
  }

  updatePreference(preference: string, value: string): void {
    const observer: PartialObserver<IMember> = {
      next: (result) => {
        this.user = result;
      },
    };
    this.memberService
      .updatePreference(preference, value)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(observer);
  }

  setValueFromToken(): void {
    //Set Current Organization
    if (!this.organization && localStorage.getItem('organization')) {
      this.organization = JSON.parse(localStorage.getItem('organization'));

      if (localStorage.getItem('user')) {
        const user = JSON.parse(localStorage.getItem('user')) as IMemberResult;

        if (user.organizations) {
          const organizations = user.organizations.map((org) => {
            return {
              address: org.organization['address'],
              name: org.organization['name'],
              id: org.organization['_id'],
              subscriptionStatus: org.organization.subscriptionStatus,
              organizationId: org.organization['_id'],
              roles: org.organization.roles,
              url: org.organization['url'],
              email: org.organization['email'],
              phone: org.organization['phone'],
              description: org.organization['description'],
              logo: org.organization['logo'],
              tags: org.organization['tags'],
            } as IOrganizationBase;
          });
        }
      }
    }
  }
  displayCampaignUrl(campaign: ISaleCampaign): string {
    return UrlUtilities.getCampaignUrl(campaign);
  }
}
